<script setup>
import useVuelidate from "@vuelidate/core"
import {email, required} from "@vuelidate/validators"
import {computed, onMounted, reactive, ref, watch} from "vue"

import InputDateTime from "@/components/form/InputDateTime.vue"
import InputText from "@/components/form/InputText.vue"
import PaymentMethodRadioGroup from '@/components/form/PaymentMethodRadioGroup.vue'
import FunnelStep from "@/components/FunnelStep.vue"
import FunnellAddress from "@/components/funnelSteps/components/FunnellAddress.vue"
import FunnelInvoiceAddress from "@/components/funnelSteps/FunnelInvoiceAddress.vue"
import FunnelStepLogin from "@/components/funnelSteps/FunnelStepLogin.vue"
import FunnelStepSummary from "@/components/funnelSteps/FunnelStepSummary.vue"
import { FunnelStepId } from '@/funnelStepId'
import {useAddressStore} from "@/stores/AddressStore"
import {useFormDataStore} from "@/stores/FormDataStore"
import {usePaymentGatewaysStore} from "@/stores/PaymentGatewaysStore"
import {useSubscriptionsStore} from "@/stores/SubscriptionsStore"
import {useUserStore} from "@/stores/UserStore"
import {useVoucherStore} from "@/stores/VoucherStore"
import {useFunnelSteps} from "@/useFunnelSteps"
import {useHelpers} from "@/useHelpers"

const addressStore = useAddressStore()
const dataStore = useFormDataStore()
const paymentGatewayStore = usePaymentGatewaysStore()
const subscriptionStore = useSubscriptionsStore()
const voucherStore = useVoucherStore()
const userStore = useUserStore()

const {getInvoiceAddress, visitUrl} = useHelpers()

const state = reactive({
  isFormVisible: true,
  isOrderInvoiceVisible: false,
  isPromoCodeVisible: false,
  isInvoiceAddressEditing: false,
  steps: [
    {
      id: FunnelStepId.SUBSCRIPTION,
      isActive: false,
      isCompleted: true,
    },
    {
      id: FunnelStepId.ACCOUNT,
      isActive: true,
      isCompleted: false,
    },
    {
      id: FunnelStepId.GIFTED_INFO,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.ADDRESS,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.PAYMENT_METHOD,
      isActive: false,
      isCompleted: false,
    },
    {
      id: FunnelStepId.SUMMARY,
      isActive: false,
      isCompleted: false,
    },
  ],
  address: {
    first_name: "",
    last_name: "",
    address: "",
    number: "",
    zip: "",
    city: "",
    country: window.settings.version,
    phone_number: "",
    company_name: "",
  },
  invoiceAddress: getInvoiceAddress(addressStore.invoiceAddress),
  email: addressStore.email,
  giftedEmail: '',
  giftedStartDate: '',
  promoCode: '',
  paymentGateway: '',
})

const funnelAddress = ref(null)

const $vGifted = useVuelidate({
  giftedEmail: {required, email},
  giftedStartDate: {required},
}, state)

const $vVoucherCode = useVuelidate({
  promoCode: {required}
}, state)

const {
  handleCompeteStep,
  handleChangeStep,
  assignStepsOrder,
  subscriptionStep,
  accountStep,
  giftedInfoStep,
  addressStep,
  paymentMethodStep,
  summaryStep
} = useFunnelSteps(state.steps)

const addressShort = computed(() => {
  if (!isPrint.value) {
    return state.giftedEmail
  }

  return `${state.address.first_name} ${state.address.last_name},
              ${state.address.address},
              ${state.address.city}`
})

const invoiceAddressShort = computed(() => {
  return `${state.invoiceAddress.first_name} ${state.invoiceAddress.last_name},
              ${state.invoiceAddress.address},
              ${state.invoiceAddress.city}`
})

const isPromoCodeValid = computed(() => {
  if (state.isPromoCodeVisible) {
    return !!voucherStore.voucher
  }
  return true
})

const isPrint = computed(() => subscriptionStore.hasContentAccess(dataStore.selectedSubscription.code, 'print'))

const handleGiftedUpdate = () => {
  $vGifted.value.$touch()

  let validAddress = true
  if (isPrint.value) {
    validAddress = funnelAddress.value.isValid()
  }

  if (!$vGifted.value.$invalid && (!isPrint.value || validAddress)) {
    handleCompeteStep(FunnelStepId.GIFTED_INFO)
  }
}

const handlePaymentGatewayUpdate = () => {
  if (state.isPromoCodeVisible) {
    $vVoucherCode.value.$touch()

    if ($vVoucherCode.value.$invalid || !voucherStore.voucher) {
      return
    }
  }

  handleCompeteStep(FunnelStepId.PAYMENT_METHOD)
}

onMounted(() => {
  assignStepsOrder(state.steps)

  if (addressStore.email) {
    //handleCompeteStep(FunnelStepId.ACCOUNT)
    state.isFormVisible = false

    //TMP: redirect to eshop
    const productID = dataStore.selectedSubscription.product.id
    const url = `products/shop/show/${productID}`
    visitUrl(url)
  }

  state.paymentGateway = paymentGatewayStore.first.code
  state.giftedStartDate = subscriptionStore.hasContentAccess(dataStore.selectedSubscription.code, 'print')
      ? (dataStore.editorialPlanFirstDate)
      : ''

  dataStore.setAddress(state.address)
  dataStore.setSendInvoice(state.isOrderInvoiceVisible)
  dataStore.setInvoiceAddress(state.invoiceAddress)
})

watch(
    () => state.isOrderInvoiceVisible,
    (visible) => {
      if (!visible) {
        state.isInvoiceAddressEditing = false
      }
    }
)

watch(
    () => state.paymentGateway,
    (value) => {
      return dataStore.setSelectedPaymentGateway(value)
    }
)

watch(
    () => state.isOrderInvoiceVisible,
    (sendInvoice) => dataStore.setSendInvoice(sendInvoice)
)

watch(
    () => state.giftedEmail,
    (email) => dataStore.setGiftedEmail(email)
)
watch(
    () => state.giftedStartDate,
    (date) => dataStore.setGiftedStartDate(date)
)

watch(
    () => state.address,
    (newAddress) => {
      dataStore.setAddress(newAddress)
      addressStore.addressDraft = newAddress
    },
    {deep: true}
)

watch(
    () => state.invoiceAddress,
    (newInvoiceAddress) => {
      dataStore.setInvoiceAddress(newInvoiceAddress)
      addressStore.invoiceAddressDraft = newInvoiceAddress
    },
    {deep: true}
)

watch(
  () => voucherStore.freeVoucher,
  (free) => {
    state.paymentGateway = free
      ? paymentGatewayStore.freeGatewayCode
      : paymentGatewayStore.first.code
  },
)

</script>

<template>
  <template v-if="state.isFormVisible">
    <FunnelStep
        :id="subscriptionStep.order"
        :details="$t('Subscription')"
        :name="dataStore.selectedSubscription.user_label"
        :is-completed="subscriptionStep.isCompleted"
        :is-active="subscriptionStep.isActive"
        @on-change-clicked="dataStore.setSelectedSubscription('')"
    />

    <FunnelStepLogin
        :id="accountStep.order"
        :details="$t('Account')"
        :name="userStore.currentEmail || state.email"
        :is-active="accountStep.isActive"
        :is-completed="accountStep.isCompleted"
        @on-click-complete-step="handleCompeteStep(FunnelStepId.ACCOUNT)"
        @on-change-clicked="handleChangeStep(FunnelStepId.ACCOUNT)"
    />

    <FunnelStep
        :id="giftedInfoStep.order"
        v-if="!giftedInfoStep.isHidden"
        :name="addressShort"
        :details="$t('Information about the recipient')"
        :is-active="giftedInfoStep.isActive"
        :is-completed="giftedInfoStep.isCompleted"
        @on-change-clicked="handleChangeStep(FunnelStepId.GIFTED_INFO)"
    >
      <div class="step-address__form">
        <InputText
            v-model="$vGifted.giftedEmail.$model"
            type="email"
            :validator="$vGifted.giftedEmail"
            :label="$t('Email of the recipient')"
        />

        <div
            v-if="isPrint"
            class="input-group"
        >
          <label for="subscription_start" class="input-label">
            {{ $t('Start of subscription') }}
          </label>
          <select
              id="subscription_start"
              style="padding: 1.2rem 2.4rem;
            border: 0.2rem solid var(--ds-divider);
            background-color: var(--ds-surface-primary);
            font-size: 1.6rem;
            line-height: 1.92rem;
            font-weight: 500;
            font-family: 'Archivo';
            transition: all 0.3s ease;
            display: block;
            border-radius: 0;
            width: 100%;
            color: var(--ds-color-text-secondary);"
              class="input-group__input"
              v-model="state.giftedStartDate"
          >
            <option
                :key="key"
                :value="key"
                v-for="(value, key) in dataStore.editorialPlan"
            >{{ value }}
            </option>
          </select>
        </div>

        <InputDateTime
            v-else
            :label="$t('Start of subscription')"
            v-model="$vGifted.giftedStartDate.$model"
            :validator="$vGifted.giftedStartDate"
        />
      </div>

      <FunnellAddress
          ref="funnelAddress"
          v-if="subscriptionStore.hasContentAccess(dataStore.selectedSubscription.code, 'print')"
          v-model="state.address"
          :submit-label="$t('Continue to billing address')"
          @on-successful-update="handleGiftedUpdate"
          is-submit-hidden
      />


      <div class="input-group button-group">
        <button
            @click="handleGiftedUpdate"
            class="button button--large button--primary step-address__button"
        >
          {{ $t('Continue to billing address') }}
        </button>
      </div>
    </FunnelStep>

    <FunnelStep
        :id="addressStep.order"
        v-if="!addressStep.isHidden"
        :name="invoiceAddressShort"
        :details="$t('Billing address')"
        :is-active="addressStep.isActive"
        :is-completed="addressStep.isCompleted"
        @on-change-clicked="handleChangeStep(FunnelStepId.ADDRESS)"
    >
      <FunnelInvoiceAddress
          v-model="state.invoiceAddress"
          :submit-label="$t('Continue to payment method')"
          @on-successful-update="handleCompeteStep(FunnelStepId.ADDRESS)"
      />
    </FunnelStep>

    <FunnelStep
        :id="paymentMethodStep.order"
        :name="paymentGatewayStore.getGatewayByCode(state.paymentGateway)?.name || ''"
        :details="$t('Payment method')"
        :is-active="paymentMethodStep.isActive"
        :is-completed="paymentMethodStep.isCompleted"
        @on-change-clicked="handleChangeStep(FunnelStepId.PAYMENT_METHOD)"
    >
      <div class="step-payment-method__form">
        <PaymentMethodRadioGroup
          v-if="!voucherStore.freeVoucher"
          v-model:paymentGateway="state.paymentGateway"
        />
      </div>

      <div v-if="!state.isInvoiceAddressEditing && isPromoCodeValid" class="input-group button-group">
        <button
            @click="handlePaymentGatewayUpdate"
            class="button button--large button--primary step-payment-method__button"
        >
          {{ $t('Continue to the summary') }}
        </button>
      </div>
    </FunnelStep>

    <FunnelStepSummary
        :id="summaryStep.order"
        :is-active="summaryStep.isActive"
        :is-completed="summaryStep.isCompleted"
    />
  </template>
  <template v-else>
    <div style="display: flex; justify-content: center; align-items: center; height: 250px;">
      <svg style="width: 100px; height: 100px;" xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'><radialGradient id='a6' cx='.66' fx='.66' cy='.3125' fy='.3125' gradientTransform='scale(1.5)'><stop offset='0' stop-color='#000000'></stop><stop offset='.3' stop-color='#000000' stop-opacity='.9'></stop><stop offset='.6' stop-color='#000000' stop-opacity='.6'></stop><stop offset='.8' stop-color='#000000' stop-opacity='.3'></stop><stop offset='1' stop-color='#000000' stop-opacity='0'></stop></radialGradient><circle transform-origin='center' fill='none' stroke='url(#a6)' stroke-width='15' stroke-linecap='round' stroke-dasharray='200 1000' stroke-dashoffset='0' cx='100' cy='100' r='70'><animateTransform type='rotate' attributeName='transform' calcMode='spline' dur='2' values='360;0' keyTimes='0;1' keySplines='0 0 1 1' repeatCount='indefinite'></animateTransform></circle><circle transform-origin='center' fill='none' opacity='.2' stroke='#000000' stroke-width='15' stroke-linecap='round' cx='100' cy='100' r='70'></circle></svg>
    </div>
  </template>
</template>